import { useEffect, useRef, useState } from 'react'

import Background from '@/components/Background/Background'
import Logo from '@/components/Logo/Logo'
import { getHomeCompetitor, getAwayCompetitor } from '@/services/jsonDataService'
import { DISPLAY_STATE } from '@/types'
import { POSITION_ANIMATIONS } from '@/utils/scorebugPosition'

import BaseballBar from './BaseballBar'
import BaseballBug from './BaseballBug'
import './BaseballTemplate.css'

import type { JsonMatchData, ScoreBugPosition } from '@/services/jsonDataService'
import type { MatchData } from '@/types'
import type { FC } from 'react'

const ANIMATION_DURATION = 500 // ms — matches useTemplateAnimation duration

export interface BaseballTemplateProps {
  matchData?: MatchData
  customerId: string
  eventGuid: string
  jsonData: JsonMatchData | null
  displayState: DISPLAY_STATE
  scoreBugPosition: ScoreBugPosition
  isVisible?: boolean
}

const BaseballTemplate: FC<BaseballTemplateProps> = ({
  matchData,
  jsonData,
  displayState,
  scoreBugPosition,
  isVisible = true,
}) => {
  const matchConfig = matchData || {}

  // Get home and away competitors
  const homeCompetitor = getHomeCompetitor(jsonData)
  const awayCompetitor = getAwayCompetitor(jsonData)

  // Use JSON data for team names, fallback to API data
  const homeTeam = homeCompetitor?.name || matchConfig.homeTeamConfig?.name || 'Home Team'
  const awayTeam = awayCompetitor?.name || matchConfig.awayTeamConfig?.name || 'Away Team'

  const targetPosition = scoreBugPosition

  // Reposition animation: exit old position → apply new position → enter
  const [activePosition, setActivePosition] = useState<ScoreBugPosition>(targetPosition)
  const [bugVisible, setBugVisible] = useState(true)
  const isRepositioning = useRef(false)

  useEffect(() => {
    if (targetPosition === activePosition || isRepositioning.current) return

    const exitAnim = POSITION_ANIMATIONS[activePosition].out
    const enterAnim = POSITION_ANIMATIONS[targetPosition].in
    console.log(`[Scorebug] position: ${activePosition} → ${targetPosition} | exit: ${exitAnim} | enter: ${enterAnim}`)

    // Start exit animation with current position
    isRepositioning.current = true
    setBugVisible(false)

    // After exit completes, switch position and re-enter
    const timer = setTimeout(() => {
      setActivePosition(targetPosition)
      setBugVisible(true)
      isRepositioning.current = false
    }, ANIMATION_DURATION)

    return () => {
      clearTimeout(timer)
      isRepositioning.current = false
    }
  }, [targetPosition, activePosition])

  // Combine parent visibility with reposition visibility
  const effectiveBugVisible = isVisible && bugVisible

  return (
    <Background>
      <div className="baseball-container">
        {/* Top section with logos */}
        <div className="baseball-logo-container">
          <Logo matchConfig={matchConfig} slotId={1} teamName={homeTeam} />
          <Logo matchConfig={matchConfig} slotId={2} teamName={awayTeam} />
        </div>

        {/* Render Bug or Bar based on display state */}
        {displayState === DISPLAY_STATE.SCOREBAR ? (
          <BaseballBar matchData={matchData} jsonData={jsonData} isVisible={isVisible} />
        ) : (
          <BaseballBug
            matchData={matchData}
            jsonData={jsonData}
            isVisible={effectiveBugVisible}
            scorebugPosition={activePosition}
          />
        )}
      </div>
    </Background>
  )
}

export default BaseballTemplate
